import { useServerDataGridState } from './grid/useServerDataGridState';
import { useServerTableColumns } from './grid/useServerTableColumns';
import type { BuildServerDataGridStateParams } from './grid/serverDataGridStateParams';
import type { UseServerDataGridStateResult } from './grid/useServerDataGridState';
import type { DataGridProps } from '../primitives/DataGrid';
import type { GridColDef, GridValidRowModel } from '@/ui/primitives';

type TableInteractionMode = 'interactive' | 'static';

const shouldSuppressGridFocusOutline = (
  interactionMode: TableInteractionMode,
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'],
): boolean => interactionMode === 'static' || typeof onColumnHeaderClick === 'function';

type UseServerDataTableGridModelArgs<T extends GridValidRowModel> = {
  stateParams: BuildServerDataGridStateParams<T>;
  columns: Array<GridColDef<T>>;
  interactionMode?: TableInteractionMode;
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'];
};

type UseServerDataTableGridModelResult<T extends GridValidRowModel> =
  UseServerDataGridStateResult<T> & {
    columnsWithWidths: Array<GridColDef<T>>;
    suppressFocusOutline: boolean;
  };

export const useServerDataTableGridModel = <T extends GridValidRowModel>({
  stateParams,
  columns,
  interactionMode = 'interactive',
  onColumnHeaderClick,
}: UseServerDataTableGridModelArgs<T>): UseServerDataTableGridModelResult<T> => {
  const gridState = useServerDataGridState<T>(stateParams);
  const columnsWithWidths = useServerTableColumns(columns, gridState.columnWidthModel);

  return {
    ...gridState,
    columnsWithWidths,
    suppressFocusOutline: shouldSuppressGridFocusOutline(interactionMode, onColumnHeaderClick),
  };
};
